// R5 — `rubricReadShape`: THE STRUCTURAL VALIDATOR FOR `RubricReadPublicV1`, SHARED BY BOTH SIDES OF THE WIRE.
//
// WHY THIS MODULE EXISTS AS ITS OWN FILE (R5.2 round-2 finding 3). The validator used to live inside
// `rubricReadStore.ts`, beside the D1 row readers and the guard batches — so the CLIENT, which is the other party
// that has to decide whether a payload is servable, could not reach it without dragging the worker's persistence
// layer into a browser bundle. The client therefore did not validate at all: it accepted any `200` body with a
// truthy `read` through a TypeScript cast and walked straight into `read.presentation`, which is a crash or a
// partial render on a partially-deployed backend rather than the registered `CS-ERR-READ-UNAVAILABLE`.
//
// THIS FILE IS PURE BY CONSTRUCTION — no D1, no Hono, no environment, no I/O. Its only imports are the DTO's own
// types plus `COPY` (a constant record) and the closed-union constants, exactly like `rubricCopy.ts`, which the
// client already imports directly. One validator, one set of transcribed unions, both sides.
//
// TWO EXPORTED PREDICATES, ONE CORE, AND THE BOUNDARY BETWEEN THEM IS §1.2a's STORAGE RULE:
//   * `isValidStoredShape`  — what `rubric_reads.public_json` may hold. The serve-time ENVELOPE KEYS MUST BE
//                             ABSENT: a stored row carrying them would be asserting, at write time, a
//                             relationship that is only true at serve time.
//   * `isValidPublicShape`  — what a served `{read: …}` body must be: the stored shape PLUS the envelope, every
//                             envelope field present and in its union.
import {
  OVERLAY_STATUSES, STALE_REASON_ORDER,
  type RubricReadPublicV1, type RubricReadStoredV1,
} from './rubricReadPublic';
import { COPY } from './rubricCopy';
// C2b (R5.7b fix) — the STRICT closed-world checker the engine and the recommendation builder already use: a scope
// is well-formed AND non-empty (`entry_ids_assessed.length > 0`, `n_assessed` agreeing). One checker, both sides.
import { checkSearchScope } from './factLedger';
// C2b — which facet level IS the L1 (omission) decision per facet; the same table the engine stamps scopes by.
import { FACET_L1_LEVEL } from './entryTypeProfiles';

/* ================================================================================================================
 * THE STORED-SCHEMA BOUNDARY (finding 6).
 *
 * A stored row is UNTRUSTED INPUT to the serve path. It was written by some earlier deploy of this code, and the
 * only thing that makes "the served payload is the §1.2a projection" true at serve time is a check performed HERE,
 * at serve time — not the fact that the writer intended to project. `serveRubricRead` spreads whatever it is given
 * straight into the response, so a row carrying `trace`, `RecomputationLog`, `alignment_confidence` or `rules_fired`
 * ships them unless this validator refuses the row.
 *
 * TWO HALVES, BOTH REQUIRED:
 *   * STRUCTURAL — every field `RubricReadStoredV1` requires is present and of the right kind. A row missing
 *     `domains` is not "a read with no domains"; it is corruption, and state X is the honest answer.
 *   * DENIED-PATH — a recursive sweep for any deny-listed key ANYWHERE in the object. Enumerated rather than
 *     inferred: the deny list is the same closed set `rubricReadPublic.ts` projects away, and a key that appears
 *     after projection can only have arrived by corruption or by a projection regression. Either way the row is
 *     refused rather than partially cleaned — cleaning would serve a payload no compute ever produced.
 * ============================================================================================================== */

/**
 * Every key the §1.2a projection DELETES, plus the two internal structures that must never leave the server. A key
 * from this set found at any depth of a stored payload sends the row to state X.
 */
export const DENIED_STORED_KEYS: readonly string[] = [
  'trace',
  'rules_fired',
  'RecomputationLog',
  'recomputation_log',
  'alignment_confidence',
  'unassessed_signals',
  'overall_rule_id',
  'rule_id',
  'criterion_label',
  // The serve-time envelope. It is assembled by `serveRubricRead` and is NOT part of stored content; a stored row
  // carrying it would be asserting a freshness relationship fixed at write time — the exact lie §1.2a's storage
  // boundary exists to prevent.
  'freshness',
  'stale_reason',
  // `overlay_status` JOINED THE ENVELOPE in the rev-14 amendment (R5.2 round-2 finding 2). It was stored, derived
  // once at projection time — which made `'superseded'` unreachable in production (a fresh compute can only pin
  // the loader's CURRENT release) and left an already-stored specialty read reporting `'current'` forever after
  // its release expired or rolled over. It is a RELATIONSHIP between an immutable pinned release id and the
  // registry as it stands NOW, which is the same reason `freshness` cannot be stored, so it is refused here for
  // the same reason and by the same mechanism.
  'overlay_status',
];

const DENIED_SET: ReadonlySet<string> = new Set(DENIED_STORED_KEYS);

/**
 * The synthetic marker returned when the sweep hits its depth ceiling.
 *
 * IT FAILS CLOSED (round-3 finding 4). Returning `null` past the ceiling means "no denied key", so a payload that
 * buries `trace` at depth 65 was served intact — the deny list was defeated by nesting, which is the one thing a
 * corrupt or regressed writer can trivially arrange. Past the ceiling the honest answer is "this validator cannot
 * vouch for what is down there", and an unvouched-for payload is corrupt: state X, never a partial serve. No
 * legitimate projected payload comes anywhere near 64 levels, so this cannot fire on real content.
 */
export const DEPTH_LIMIT_MARKER = '__depth_limit_exceeded__';

/** Recursive denied-key sweep. Returns the first offending key, or null. */
export function findDeniedKey(node: unknown, depth = 0): string | null {
  if (node === null || typeof node !== 'object') return null;
  if (depth > 64) return DEPTH_LIMIT_MARKER;
  if (Array.isArray(node)) {
    for (const item of node) {
      const hit = findDeniedKey(item, depth + 1);
      if (hit) return hit;
    }
    return null;
  }
  for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
    if (DENIED_SET.has(k)) return k;
    const hit = findDeniedKey(v, depth + 1);
    if (hit) return hit;
  }
  return null;
}

const isObj = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);

/* ---------------------------------------------------------------------------------------------------------------
 * THE STRUCTURAL VALIDATOR — EXHAUSTIVE, walked off the PROJECTED TYPES (round-3 finding 3).
 *
 * The previous shape checked a dozen selected fields and trusted the rest, so a row missing
 * `facet_framing_copy_string_id`, or carrying `band: "excellent"`, or an overlay row with no `prevalence`, was
 * accepted and SERVED — structurally incomplete stored data reaching the client instead of state X. Every required
 * field of `RubricReadStoredV1` (= ProjectedAssessment + ReadIdentity + overlay/presentation/recommendations) is
 * checked here, and every closed union is checked against its members rather than against `typeof === 'string'`.
 *
 * The unions below are TRANSCRIBED from the engine's own type declarations. `tsc` cannot check a runtime array
 * against a type alias, so the corpus test in tests/rubricReadPublic.test.ts — which runs the real engine and
 * feeds its projected output through this validator — is what pins them to reality: an engine union that gains a
 * member and is not added here fails that test rather than silently rejecting live payloads in production.
 * ------------------------------------------------------------------------------------------------------------ */

const DOMAIN_IDS = ['clinical', 'leadership', 'scholarship', 'service', 'teaching'] as const;
const CONTENT_BANDS = ['not_shown', 'limited', 'demonstrated', 'substantial'] as const;
const RESPONSIBILITY_LEVELS = [
  'role_not_stated', 'stated_participation', 'stated_defined_responsibility', 'stated_direction',
] as const;
const COMPLETION_LEVELS = [
  'status_not_stated', 'stated_activity_under_way', 'stated_completed_deliverable',
  'stated_dissemination_use_or_recognition',
] as const;
const CONTINUITY_LEVELS = [
  'single_occasion_or_repeated_span_not_established', 'stated_repeated_under_12_months',
  'stated_involvement_12_months_or_more', 'stated_change_in_role_over_time',
] as const;
const CONFIDENCE_LEVELS = ['high', 'moderate', 'low'] as const;
const INPUT_SURFACES = ['custom_cv', 'carms_my_information', 'other_resume'] as const;
const SUB_ELEMENT_IDS = ['navigability', 'chronology', 'status_labelling', 'attribution', 'consistency'] as const;
const PRESENTATION_BANDS = ['not_assessable', 'limited', 'adequate', 'strong'] as const;
const PREVALENCE_CLASSES = ['most', 'some', 'few', 'none'] as const;
const DOCUMENTATION_CLASSES = ['full', 'partial', 'none'] as const;
const MAPPING_CERTAINTIES = ['high', 'low'] as const;
const RECOMMENDATION_KINDS = ['known_fact', 'question_to_answer', 'suggested_phrasing'] as const;
const FLAG_TYPES = [
  'status_not_stated', 'contribution_not_stated', 'clinical_role_context_not_stated',
] as const;
const FACET_EXPECTATIONS = ['expected', 'not_expected', 'unknown'] as const;

const isStr = (v: unknown): v is string => typeof v === 'string';
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isBool = (v: unknown): v is boolean => typeof v === 'boolean';
const isStrArray = (v: unknown): boolean => Array.isArray(v) && v.every(isStr);
const inUnion = (v: unknown, members: readonly string[]): boolean => isStr(v) && members.includes(v);
const orNull = (v: unknown, ok: (x: unknown) => boolean): boolean => v === null || ok(v);
/**
 * Every registered copy-string id, built once at module init from the SAME `COPY` object `rubricCopy.ts` renders
 * from. A row carrying a `copy_string_id` this set does not contain is not "an id we haven't rendered yet" — it is
 * either corruption or a copy id from a deploy whose registry has since dropped it, and either way there is no
 * text this server can resolve it to.
 */
const REGISTERED_COPY_IDS: ReadonlySet<string> = new Set(Object.keys(COPY));

/**
 * C2b — the SCORING_VERSION at which per-entry closed-world scopes were introduced (R5.7+R5.8 verify round 2).
 * A read at or above it must carry a strict scope on every omission claim; an older stored row keeps them optional.
 * Pinned as its own number (not imported from the engine) so that a later SCORING_VERSION bump cannot move the
 * boundary: rows written at 7 are scoped, whatever the current engine version is.
 */
export const SCOPE_REQUIRED_FROM_VERSION = 7;

/**
 * R7.1 (Sol round 2, blocker 1) — the SCORING_VERSION at which the §6b `convention` KEY became part of the
 * projection's own output rather than an optional attachment.
 *
 * WHY A PRESENCE RULE AND NOT A NON-NULL RULE. `runRubricCompute` emits `convention` on EVERY read from version 8
 * on — `null` when the CV is generic, the layer is off, the specialty has no ratified set, or its set is switched
 * off, and the panel otherwise. `null` is therefore a REAL, load-bearing value ("this read looked and there is no
 * panel"), exactly as `overlay: null` is; requiring non-null would refuse every generic read the engine produces.
 * What was wrong before is that ABSENCE was accepted at every version: `JSON.stringify` drops an `undefined` key,
 * so a v8 row with no `convention` key is a row a projection that predates (or regressed out of) §6b wrote, and
 * serving it means answering "did this read consider the convention layer?" with silence. From version 8 the key
 * must be there; at 7 and below it may be absent, because those projections genuinely had no such key. A key that
 * is present but MALFORMED is refused at EVERY version — that has always been true and stays true.
 */
export const CONVENTION_REQUIRED_FROM_VERSION = 8;

/** A copy-string id: a non-empty string that is an actual member of the copy registry. */
const isCopyId = (v: unknown): boolean => isStr(v) && v.length > 0 && REGISTERED_COPY_IDS.has(v);

/** `SearchScope` — the closed-world evidence record (factLedger.ts). */
function isSearchScope(v: unknown): boolean {
  return isObj(v)
    && isStr(v.claim_type) && isStr(v.scope_description)
    && isStrArray(v.entry_ids_assessed) && isNum(v.n_assessed) && isStr(v.extractor_version);
}

/** A STRICT `SearchScope` (C2b): well-formed, non-empty enumeration, count agreeing — `checkSearchScope`. */
const isStrictSearchScope = (v: unknown): boolean => isSearchScope(v) && checkSearchScope(v).ok;

/**
 * C2b — the per-entry closed-world scope on an L1 facet decision or a clarification flag.
 *
 * FAIL-CLOSED END-TO-END (R5.7b fix, Claude+Sol adjudicated 2026-08-22): a row computed at SCORING_VERSION ≥ 7
 * (the version that introduced the scope) MUST carry a STRICT scope on every L1 facet decision and every flag — a
 * missing, null, malformed or EMPTY scope fails the shape, so the claim can never reach a renderer without the
 * closed world it was assessed over. A row stored BEFORE the field existed (`scoring_version < 7`) keeps the key
 * optional — absent means the KEY is absent; an explicit `null` is not a scope and is rejected either way, and a
 * present scope must be a full `SearchScope`.
 */
function isScopeOk(v: unknown, required: boolean): boolean {
  if (v === undefined) return !required;
  return required ? isStrictSearchScope(v) : isSearchScope(v);
}

function isProjectedFacet(v: unknown, levels: readonly string[], l1Level: string, strict: boolean): boolean {
  if (!isObj(v) || !inUnion(v.level, levels) || !isCopyId(v.copy_string_id) || !isStrArray(v.evidence_fact_ids)) return false;
  // Only the L1 decision is an omission claim; an L2+ level carries spans, and a scope on it is tolerated only in
  // the shape it would have had (never null, never malformed) — it is never REQUIRED there.
  return isScopeOk(v.search_scope, strict && v.level === l1Level);
}

function isProjectedConfidence(v: unknown): boolean {
  return isObj(v) && inUnion(v.level, CONFIDENCE_LEVELS) && isCopyId(v.copy_string_id);
}

function isDescriptorValue(v: unknown, valueOk: (x: unknown) => boolean): boolean {
  return isObj(v) && valueOk(v.value) && isStrArray(v.evidence_fact_ids);
}

function isDescriptors(v: unknown): boolean {
  if (!isObj(v)) return false;
  for (const k of ['venue_or_award_scopes', 'recognition_types', 'topic_tags', 'dissemination_event_types'] as const) {
    if (!Array.isArray(v[k]) || !(v[k] as unknown[]).every((d) => isDescriptorValue(d, isStr))) return false;
  }
  if (!Array.isArray(v.trajectory)) return false;
  const trajectoryOk = (v.trajectory as unknown[]).every((d) => isDescriptorValue(d, (x) =>
    isObj(x) && inUnion(x.domain, DOMAIN_IDS) && Array.isArray(x.years) && (x.years as unknown[]).every(isNum)));
  if (!trajectoryOk) return false;
  // `recurrence` is a SINGLE nullable descriptor, not an array — the one asymmetry in this object.
  if (!orNull(v.recurrence, (x) => isDescriptorValue(x, (y) =>
    // `DatePartial` is `string | 'not_stated'`, i.e. a string either way.
    isObj(y) && isStr(y.frequency) && isStr(y.date_start) && isStr(y.date_end)))) return false;
  if (!orNull(v.collaboration, (x) =>
    isObj(x) && x.copy_string_id === 'CS-DESC-COLLABORATION' && isStr(x.evidence_fact_id))) return false;
  return true;
}

function isClarificationFlag(v: unknown, strict: boolean): boolean {
  return isObj(v)
    && inUnion(v.flag_type, FLAG_TYPES) && isStr(v.entry_id) && isCopyId(v.copy_string_id)
    && v.severity === 'clarification'
    // C2b — every flag is an omission prompt: strict scope required at SCORING_VERSION ≥ 7.
    && isScopeOk(v.search_scope, strict);
}

function isProjectedDomain(v: unknown, strict: boolean): boolean {
  return isObj(v)
    && inUnion(v.domain, DOMAIN_IDS) && isCopyId(v.domain_copy_string_id)
    && inUnion(v.band, CONTENT_BANDS) && isCopyId(v.band_copy_string_id)
    && isStrArray(v.fully_documented_unit_ids) && isStrArray(v.assigned_unit_ids)
    && isStrArray(v.qualifying_entry_ids)
    && (v.saturation_copy_string_id === null || v.saturation_copy_string_id === 'CS-SATURATION')
    && orNull(v.not_shown, (x) => isObj(x)
      && x.copy_string_id === 'CS-NOT-SHOWN' && x.extended_copy_string_id === 'CS-NOT-SHOWN-EXTENDED'
      // C2b — the "Not shown" claim's closed world: strict (non-empty, count agreeing) at SCORING_VERSION ≥ 7.
      && (strict ? isStrictSearchScope(x.search_scope) : isSearchScope(x.search_scope)))
    && isStrArray(v.evidence_fact_ids);
}

/**
 * R5.8 — the `expectations` projection. OPTIONAL on a stored row: a read computed before SCORING_VERSION 7 has no
 * such field and is still a valid stored shape (it reads as generic and `freshness` already names the version
 * drift). When present it must be exactly the closed shape the engine emits.
 */
function isEntryExpectations(v: unknown): boolean {
  if (v === undefined) return true;
  return isObj(v)
    && isStr(v.profile_id) && v.profile_id.length > 0
    && isStr(v.sub_type)
    && inUnion(v.responsibility, FACET_EXPECTATIONS)
    && inUnion(v.completion, FACET_EXPECTATIONS)
    && inUnion(v.continuity, FACET_EXPECTATIONS);
}

function isProjectedEntry(v: unknown, strict: boolean): boolean {
  return isObj(v)
    && isStr(v.entry_id)
    && isEntryExpectations(v.expectations)
    && isProjectedFacet(v.responsibility, RESPONSIBILITY_LEVELS, FACET_L1_LEVEL.responsibility, strict)
    && isProjectedFacet(v.completion, COMPLETION_LEVELS, FACET_L1_LEVEL.completion, strict)
    && isProjectedFacet(v.continuity, CONTINUITY_LEVELS, FACET_L1_LEVEL.continuity, strict)
    && isBool((v.continuity as Record<string, unknown>).also_12_months_or_more)
    && v.facet_framing_copy_string_id === 'CS-FACET-FRAMING'
    && (v.observership_copy_string_id === null || v.observership_copy_string_id === 'CS-OBSERVERSHIP')
    && Array.isArray(v.domains) && (v.domains as unknown[]).every((d) => inUnion(d, DOMAIN_IDS))
    && isDescriptors(v.descriptors)
    && isProjectedConfidence(v.confidence)
    && isBool(v.signals_incomplete)
    && Array.isArray(v.flags) && (v.flags as unknown[]).every((f) => isClarificationFlag(f, strict));
}

function isProjectedPresentation(v: unknown): boolean {
  if (!isObj(v)) return false;
  if (!Array.isArray(v.sub_elements)) return false;
  const subsOk = (v.sub_elements as unknown[]).every((s) => isObj(s)
    && inUnion(s.sub_element, SUB_ELEMENT_IDS) && isCopyId(s.copy_string_id)
    && inUnion(s.band, PRESENTATION_BANDS) && isCopyId(s.band_copy_string_id)
    && isNum(s.applicable_items) && isNum(s.satisfying_items)
    && isStrArray(s.evidence_layout_fact_ids) && isBool(s.excluded_from_aggregation));
  if (!subsOk) return false;
  if (!orNull(v.overall, (x) => inUnion(x, PRESENTATION_BANDS))) return false;
  if (!orNull(v.overall_copy_string_id, isCopyId)) return false;
  return v.split_output_copy_string_id === 'CS-SPLIT-OUTPUT';
}

function isRenderedString(v: unknown): boolean {
  return isObj(v) && isCopyId(v.copy_string_id) && isStr(v.text);
}

/**
 * `PdSurveyCitation` (specialtyProfiles.ts) — `{citation: string, year: number}`. Empty in every shipped profile
 * today (§6.2 source 2's per-criterion attachment has no populated release yet), which is exactly why this must be
 * validated rather than merely typed: `Array.isArray` alone accepts `[null]` or `[{}]` and the corpus test that
 * would have caught it never exercises a non-empty array. `citation` must be non-empty prose, and `year` must be a
 * real publication year — an integer in a sane range, not any finite number (`NaN`-adjacent values like `1e300` or
 * fractional years are not years).
 */
function isPdSurveyCitation(v: unknown): boolean {
  return isObj(v)
    && isStr(v.citation) && v.citation.length > 0
    && isNum(v.year) && Number.isInteger(v.year) && v.year >= 1900 && v.year <= 2100;
}

function isSourceReference(v: unknown): boolean {
  return isObj(v)
    && isStr(v.source_kind) && isNum(v.program_count) && isStr(v.corpus_release) && isStr(v.cycle)
    && isStr(v.last_verified)
    && Array.isArray(v.pd_survey_citations) && v.pd_survey_citations.every(isPdSurveyCitation);
}

function isProjectedOverlay(v: unknown): boolean {
  if (!isObj(v)) return false;
  if (!isStr(v.specialty) || !isStr(v.cycle)) return false;
  if (v.hedge_copy_string_id !== 'CS-OVERLAY-HEDGE') return false;
  if (!(v.non_documentable_note_copy_string_id === null
        || v.non_documentable_note_copy_string_id === 'CS-NON-DOCUMENTABLE-CRITERIA')) return false;
  if (!Array.isArray(v.rows)) return false;
  return (v.rows as unknown[]).every((r) => isObj(r)
    && isStr(r.criterion_id)
    && inUnion(r.prevalence_class, PREVALENCE_CLASSES) && isNum(r.n_programs) && isNum(r.N_specialty)
    && isRenderedString(r.prevalence) && isSourceReference(r.source_reference)
    && inUnion(r.documentation_class, DOCUMENTATION_CLASSES) && isRenderedString(r.documentation)
    && isStrArray(r.evidence_entry_ids) && isStrArray(r.evidence_fact_ids)
    && orNull(r.search_scope, isSearchScope)
    && inUnion(r.criterion_mapping_certainty, MAPPING_CERTAINTIES)
    && (r.mapping_certainty_copy_string_id === null || r.mapping_certainty_copy_string_id === 'CS-MAPPING-UNCERTAIN')
    // Attached PER ROW by the projection, from the read's own `overlay_release_id`.
    && isStr(r.profile_release_id));
}

/**
 * R7.1 §6b — the convention panel's stored shape.
 *
 * OPTIONAL AND NULLABLE ON A STORED ROW: every read computed before R7.1, and every read computed with the layer
 * off, has no panel — absent and `null` are both valid and both mean "no §6b panel", exactly as `overlay: null`
 * means no overlay. When present it must be the closed shape the module emits, and TWO conditions are enforced
 * beyond the field types because they are the ones that carry a claim:
 *
 *   (1) A NOT-DOCUMENTED row MUST carry a STRICT closed-world scope (C2b, spec §6 T-CONV-8). No scope, no claim:
 *       the module degrades such a row to `unbound`, and a payload that carries the claim without the closed world
 *       is one this validator refuses rather than one a renderer has to remember to check.
 *   (2) A DOCUMENTED row MUST cite evidence. "Your CV documents this on {n} entries" with an empty evidence list
 *       is a positive claim with nothing behind it (C2a).
 */
function isConventionRow(v: unknown): boolean {
  if (!isObj(v)) return false;
  if (!isStr(v.signal_id) || !inUnion(v.binding, ['B0', 'B1', 'B2', 'B3', 'B4'])) return false;
  if (!orNull(v.ct_a_mapping, isStr)) return false;
  if (!isStr(v.area_label) || v.area_label.length === 0) return false;
  if (!inUnion(v.status, ['documented', 'partial', 'not_documented', 'unbound'])) return false;
  if (!isRenderedString(v.card)) return false;
  if (!orNull(v.recover, isRenderedString)) return false;
  if (!isRenderedString(v.source) || !isStr(v.ratification_doc)) return false;
  if (!isStrArray(v.evidence_entry_ids) || !isStrArray(v.evidence_fact_ids)) return false;
  if (!orNull(v.search_scope, isSearchScope)) return false;
  if (!Array.isArray(v.survey_citations) || !v.survey_citations.every(isRenderedString)) return false;
  if (!orNull(v.corpus_note, isRenderedString)) return false;
  if (v.status === 'not_documented' && !isStrictSearchScope(v.search_scope)) return false;
  if (v.status === 'documented' && (v.evidence_fact_ids as string[]).length === 0) return false;
  // A row that is NOT an omission claim must not carry an omission claim's scope.
  if (v.status !== 'not_documented' && v.search_scope !== null) return false;
  return true;
}

function isConventionSection(v: unknown, rowsAllowed: boolean): boolean {
  if (!isObj(v)) return false;
  if (!isCopyId(v.heading_copy_string_id) || !isCopyId(v.hedge_copy_string_id)) return false;
  if (typeof v.owner_reviewed !== 'boolean') return false;
  if (!Array.isArray(v.rows)) return false;
  if (!rowsAllowed && v.rows.length > 0) return false;
  if (rowsAllowed && !v.rows.every(isConventionRow)) return false;
  if (!orNull(v.empty_copy_string_id, isCopyId)) return false;
  // NEVER SILENT: a section with no rows states why, through its registered empty string (T-CONV-9).
  if (v.rows.length === 0 && v.empty_copy_string_id === null) return false;
  if (v.rows.length > 0 && v.empty_copy_string_id !== null) return false;
  return true;
}

function isConventionPanel(v: unknown, required: boolean): boolean {
  // ABSENT — legal only BELOW `CONVENTION_REQUIRED_FROM_VERSION`. From 8 on, a row with no key at all was written
  // by a projection that does not know the layer exists, and it is refused rather than read as "no panel".
  if (v === undefined) return !required;
  // NULL — the honest, projection-authored "there is no §6b panel for this read". Legal at EVERY version.
  if (v === null) return true;
  if (!isObj(v)) return false;
  if (!isStr(v.specialty) || !isStr(v.signal_set_version)) return false;
  if (v.claim_copy_string_id !== 'CS-CONV-CLAIM') return false;
  if (v.footer_copy_string_id !== 'CS-CONV-NOT-A-WEIGHT') return false;
  return isConventionSection(v.convention, true)
    && isConventionSection(v.surveys, false)
    && isConventionSection(v.corpus, false);
}

function isRecommendation(v: unknown, strict: boolean): boolean {
  if (!isObj(v)) return false;
  if (!inUnion(v.kind, RECOMMENDATION_KINDS) || !isCopyId(v.kind_copy_string_id)) return false;
  if (v.horizon !== 'group_a_documentation_today') return false;
  if (!isStr(v.entry_id)) return false;
  if (!orNull(v.copy_string_id, isCopyId)) return false;
  if (!orNull(v.template_id, isStr)) return false;
  if (!orNull(v.slots, (s) => isObj(s) && Object.values(s).every((slot) => isObj(slot)
    && ((slot.kind === 'ledger' && isStr(slot.fact_id) && isStr(slot.value))
      || (slot.kind === 'placeholder' && isStr(slot.literal)))))) return false;
  const ev = v.evidence;
  if (!isObj(ev)) return false;
  // C2b — an omission-type recommendation (`question_to_answer`, closed-world evidence) requires a STRICT scope at
  // SCORING_VERSION ≥ 7; a question with facts-evidence, or a closed-world evidence with no/empty scope, fails.
  if (v.kind === 'question_to_answer' && strict) return ev.kind === 'closed_world' && isStrictSearchScope(ev.search_scope);
  return (ev.kind === 'facts' && isStrArray(ev.fact_ids))
    || (ev.kind === 'closed_world' && (strict ? isStrictSearchScope(ev.search_scope) : isSearchScope(ev.search_scope)));
}

function isBandChange(v: unknown): boolean {
  return isObj(v)
    && inUnion(v.subject, DOMAIN_IDS)
    && inUnion(v.before_band, CONTENT_BANDS) && inUnion(v.after_band, CONTENT_BANDS);
}

/**
 * THE CORE — everything both sides share: the projected assessment, the three attached structures, and the
 * persistence-identity half of the add-list. It says NOTHING about the envelope; the two exported predicates
 * below each add their own half of §1.2a's storage rule, which is the only thing that distinguishes them.
 */
function isValidCoreShape(v: unknown): v is Record<string, unknown> {
  if (!isObj(v)) return false;

  // ---- ProjectedAssessment.
  if (!isNum(v.scoring_version)) return false;
  // C2b — the per-entry scopes exist from SCORING_VERSION 7; from there on they are REQUIRED and STRICT.
  const strict = v.scoring_version >= SCOPE_REQUIRED_FROM_VERSION;
  if (!Array.isArray(v.domains) || !v.domains.every((d) => isProjectedDomain(d, strict))) return false;
  if (!Array.isArray(v.entries) || !v.entries.every((e) => isProjectedEntry(e, strict))) return false;
  if (!isProjectedConfidence(v.review_confidence)) return false;
  if (v.intended_use_copy_string_id !== 'CS-INTENDED-USE') return false;
  if (!(v.input_surface_notice_copy_string_id === null
        || v.input_surface_notice_copy_string_id === 'CS-INPUT-OTHER-RESUME')) return false;

  // ---- The three attached structures.
  if (!orNull(v.overlay, isProjectedOverlay)) return false;
  if (!isConventionPanel(v.convention, v.scoring_version >= CONVENTION_REQUIRED_FROM_VERSION)) return false;
  if (!isProjectedPresentation(v.presentation)) return false;
  if (!Array.isArray(v.recommendations) || !v.recommendations.every((r) => isRecommendation(r, strict))) return false;

  // ---- ReadIdentity.
  if (!isStr(v.read_id) || !isStr(v.ledger_id)) return false;
  for (const k of ['read_revision', 'ledger_version', 'correction_revision', 'assessed_at'] as const) {
    if (!isNum(v[k])) return false;
  }
  if (!orNull(v.overlay_release_id, isStr)) return false;
  if (v.mode !== 'generic' && v.mode !== 'specialty') return false;
  if (!inUnion(v.input_surface, INPUT_SURFACES)) return false;
  if (!orNull(v.band_changes, (b) => Array.isArray(b) && b.every(isBandChange))) return false;
  return true;
}

/**
 * THE THREE SERVE-TIME ENVELOPE KEYS (§1.2a's storage boundary, as amended in rev 14).
 *
 * `freshness` and `stale_reason` were always here. `overlay_status` joined them in the amendment: all three are
 * RELATIONSHIPS between an immutable stored row and the CURRENT canonical state, and none of them can be fixed at
 * write time without lying the moment that state moves.
 */
export const SERVE_ENVELOPE_KEYS = ['freshness', 'stale_reason', 'overlay_status'] as const;

/**
 * The structural validator for `RubricReadPublicV1`'s STORED half — what `rubric_reads.public_json` may hold.
 * Field-for-field against the projected types, so a required field that goes missing — or a union member nobody
 * can produce — is a refusal rather than an `undefined` on the wire.
 *
 * THE ENVELOPE KEYS MUST BE ABSENT. A stored row carrying one was written by a projection that put a serve-time
 * relationship on the stored side, and serving it would mean answering "is this read's pinned profile release
 * still the active one?" with whatever the answer happened to be on the day the row was written.
 */
export function isValidStoredShape(v: unknown): v is RubricReadStoredV1 {
  if (!isValidCoreShape(v)) return false;
  for (const k of SERVE_ENVELOPE_KEYS) if (k in v) return false;
  return true;
}

/**
 * The ENVELOPE-AWARE denied-key sweep (round-3 finding 1) — the same `findDeniedKey` mechanism the stored
 * boundary runs at `rubricReadStore.ts`'s `parseStored`, adapted for a SERVED body rather than a stored row.
 *
 * A served body legitimately carries the three envelope keys — `freshness`, `stale_reason`, `overlay_status` — as
 * DIRECT top-level properties; that is the one position §1.2a's envelope actually occupies. Anywhere else — nested
 * inside `domains`, `entries`, `overlay`, or repeated deeper in the tree — those same key names are not the
 * envelope, they are stored content posing as it (or a projection regression), and `DENIED_STORED_KEYS` already
 * lists all three for exactly that reason. So this sweep skips the top-level envelope positions and nothing else:
 * every other key, at every depth including the top level, goes through the same `findDeniedKey` the stored
 * boundary uses, unmodified.
 */
export function findDeniedKeyInPublicShape(v: Record<string, unknown>): string | null {
  for (const [k, val] of Object.entries(v)) {
    if ((SERVE_ENVELOPE_KEYS as readonly string[]).includes(k)) continue; // legitimate ONLY at this exact position
    if (DENIED_SET.has(k)) return k;
    const hit = findDeniedKey(val, 1);
    if (hit) return hit;
  }
  return null;
}

/**
 * The structural validator for a SERVED `{read: …}` body — the stored shape PLUS the complete envelope.
 *
 * THIS IS THE CLIENT'S ENTRY POINT (round-2 finding 3). A `200` whose body has a truthy `read` proves nothing: a
 * partially-deployed backend can answer with an older payload shape, and the surface used to accept it through a
 * bare TypeScript cast and then read `read.presentation`. Every envelope key must be PRESENT here — `null` is a
 * real `overlay_status` value ("no profile was ever involved"), so absence cannot be read as it.
 *
 * ROUND-3 FINDING 1: structural + envelope agreement is not the whole boundary. Nothing here used to run the
 * denied-key sweep, so an otherwise-valid served payload augmented with a nested `trace` or `rules_fired` passed
 * this validator even though the worker's stored-side boundary would refuse its stored form outright — contradicting
 * §12a's claim that the client "refuses exactly what the server refuses." `findDeniedKeyInPublicShape` closes that
 * gap with the SAME deny list and the SAME recursive mechanism, made envelope-aware for the one place the envelope
 * legitimately lives.
 */
export function isValidPublicShape(v: unknown): v is RubricReadPublicV1 {
  if (!isValidCoreShape(v)) return false;
  if (v.freshness !== 'current' && v.freshness !== 'stale' && v.freshness !== 'corrupt') return false;
  if (!Array.isArray(v.stale_reason) || !v.stale_reason.every((r) => inUnion(r, STALE_REASON_ORDER))) return false;
  // R5-OQ-H as amended. The KEY MUST BE PRESENT — an absent one is a backend that predates the field, and the
  // surface branches on this value to decide whether to tell an applicant their specialty criteria expired.
  if (!('overlay_status' in v)) return false;
  if (!orNull(v.overlay_status, (x) => inUnion(x, OVERLAY_STATUSES))) return false;
  if (findDeniedKeyInPublicShape(v) !== null) return false;
  return true;
}

